import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { formatSlot, loadOutreachPlan, orDash, webLink } from './adminData.js';
import { buildFirstEmail } from './outreachMessage.js';
import { EmailLink } from './ProviderProspects.jsx';
import { SortableTable } from './SortableTable.jsx';
import { sortRows } from './sorting.js';

const noRows = [];

/** The provider's name and, under it, its website as a domain to open in a new tab. */
function ProviderCell({ organisation, website }) {
  const site = webLink(website);
  return (
    <>
      {organisation}
      <span className="admin-sub">
        {site
          ? <a className="admin-ext" href={site.href} target="_blank" rel="noreferrer" title={`Open ${site.domain} in a new tab`}>{site.domain}</a>
          : 'No website'}
      </span>
    </>
  );
}

function SelfReferralPage({ source, organisation }) {
  const page = webLink(source);
  return page
    ? <a className="admin-ext" href={page.href} target="_blank" rel="noreferrer" title={`Open the page where ${organisation} takes self-referrals`}>Open</a>
    : '–';
}

/** The first email exactly as the provider received it: who it is from and to, when it went, and the message in a locked-down frame. */
function ScheduledEmail({ row }) {
  const email = useMemo(
    () => buildFirstEmail({ organisation: row.organisation, council: row.council, selfReferral: row.self_referral === true }),
    [row.organisation, row.council, row.self_referral],
  );
  return (
    <div className="admin-email-detail">
      <dl className="mod-fields">
        <dt>From</dt><dd>{email.from}</dd>
        <dt>To</dt><dd>{orDash(row.email)}</dd>
        <dt>Subject</dt><dd>{email.subject}</dd>
        <dt>Sends</dt><dd>{formatSlot(row.send_at)}</dd>
      </dl>
      <iframe className="admin-email-body" title={`Email to ${row.organisation}`} sandbox="" referrerPolicy="no-referrer" srcDoc={email.html}/>
    </div>
  );
}

/** Only the first email has a message written, so only its rows open. */
const hasMessage = (row) => Number(row.step) === 1;

/** Things in a row that do their own job when clicked, so they must not also open or close the email. */
const ownClicks = 'a, button, input, select, textarea, label, details';

/** The London day of a time, as 2026-10-28, so "today" is the day in the UK whatever the browser's clock says. */
const londonDay = (value) => new Date(value).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });

/** An email that has gone to Resend (sent, or booked to go later) and goes out on `day`. */
const sentOn = (row, day) => (row.status === 'sent' || row.status === 'scheduled') && Boolean(row.send_at) && londonDay(row.send_at) === day;

// The columns the two tables share, so they read the same.
const providerColumns = [
  { key: 'provider', label: 'Provider', value: (row) => row.organisation, cell: (row) => <ProviderCell organisation={row.organisation} website={row.website}/> },
  { key: 'council', label: 'Council', value: (row) => row.council, cell: (row) => orDash(row.council) },
  { key: 'to', label: 'Send to', value: (row) => row.email, cell: (row) => <EmailLink value={row.email}/> },
  {
    key: 'page',
    label: 'Self-referral page',
    value: (row) => Boolean(webLink(row.self_referral_source)),
    first: 'desc',
    cell: (row) => <SelfReferralPage source={row.self_referral_source} organisation={row.organisation}/>,
  },
];

const reservesShown = 8;
const reserveColumns = [{ key: 'rank', label: 'Rank', value: (row) => row.rank, cell: (row) => orDash(row.rank) }, ...providerColumns];

/**
 * Today's emails from Naima's outreach plan to housing providers (sent, or booked in Resend for later today), then the reserves.
 * A first email opens in place to show exactly what the provider received.
 * `refreshKey` changes whenever the section above is refreshed, so the plan is read again with it.
 */
export default function OutreachSchedule({ refreshKey = 0 }) {
  const [state, setState] = useState({ plan: null, error: '' });
  // The one email opened in place under its row ('' when none is).
  const [openId, setOpenId] = useState('');
  // Every request takes a number; only the newest one is allowed to change the screen.
  const latest = useRef(0);

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const plan = await loadOutreachPlan(supabase);
      if (ticket !== latest.current) return;
      setState({ plan: plan || {}, error: '' });
    } catch (cause) {
      if (ticket !== latest.current) return;
      setState((current) => ({ plan: current.plan, error: cause.message || 'Could not load the schedule. Please try again.' }));
    }
  }, []);

  useEffect(() => {
    void load();
    return () => { latest.current += 1; };
  }, [load, refreshKey]);

  const { plan, error } = state;
  const today = londonDay(Date.now());
  // In the order they went; emails sent together stay in the order the database gave them.
  const rows = useMemo(
    () => sortRows((Array.isArray(plan?.rows) ? plan.rows : noRows).filter((row) => sentOn(row, today)), (row) => row.send_at, 'asc'),
    [plan, today],
  );
  // The next eight reserves (the database gives them in rank order).
  const reserves = useMemo(() => (Array.isArray(plan?.reserves) ? plan.reserves.slice(0, reservesShown) : noRows), [plan]);

  /** The whole row opens the email; the button is how a keyboard or screen reader does the same (its click bubbles to the row). */
  const rowProps = (row) => {
    if (!hasMessage(row)) return undefined;
    const open = openId === row.id;
    return {
      className: `admin-email-row${open ? ' is-open' : ''}`,
      onClick: (event) => {
        const own = event.target.closest?.(ownClicks);
        if (own && !own.classList.contains('admin-email-toggle')) return;
        setOpenId(open ? '' : row.id);
      },
    };
  };

  const columns = [
    {
      key: 'when',
      label: 'When',
      value: (row) => row.send_at,
      // On one line, arrow and all, however many columns the table has.
      cell: (row) => (
        <span className="admin-nowrap">
          {hasMessage(row) && (
            <button type="button" className="admin-email-toggle" aria-expanded={openId === row.id} aria-label={`Show the email to ${row.organisation}`}>
              <span aria-hidden="true">{openId === row.id ? '▾' : '▸'}</span>
            </button>
          )}
          {formatSlot(row.send_at)}
        </span>
      ),
    },
    ...providerColumns,
  ];

  return (
    <>
      <h2 className="admin-section">Scheduled emails</h2>
      {error && (
        <p className="mod-error" role="alert">
          {`${error} `}
          <button type="button" className="admin-link" onClick={() => void load()}>Refresh</button>
        </p>
      )}
      {!plan && !error && <p>Loading the schedule…</p>}
      {plan && (
        <>
          {rows.length === 0
            ? <p className="admin-empty">No emails to housing providers today.</p>
            : (
              <SortableTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.id}
                rowProps={rowProps}
                detail={(row) => (openId === row.id && hasMessage(row) ? <ScheduledEmail row={row}/> : null)}
              />
            )}
          <h2 className="admin-section">Reserves</h2>
          {reserves.length === 0
            ? <p className="mod-note">No reserves left.</p>
            : <SortableTable columns={reserveColumns} rows={reserves} rowKey={(row) => row.id}/>}
        </>
      )}
    </>
  );
}
